import { DashboardUseCaseCounts } from '@api';
import { MapperHelper } from '@helpers/mapper';
import React, { useMemo } from 'react';

import { StackedPill } from '../StackedPill/StackedPill';

import styles from './RiskAssessmentSection.module.scss';

interface DatasetType {
  data: number[];
  backgroundColor: string;
  hoverBackgroundColor: string;
  borderSkipped?: boolean;
  datasetIndex?: number;
}

interface RiskAssessmentSectionProps {
  riskAssessment: DashboardUseCaseCounts['riskAssessment'];
}

export const RiskAssessmentSection: React.FC<RiskAssessmentSectionProps> = ({ riskAssessment }) => {
  const hasRiskAssessment = Object.keys(riskAssessment).length > 0;
  const keys = Object.keys(riskAssessment);
  const maxIndex = Math.max(keys.length - 1, 1);

  const getColor = (index: number) => MapperHelper.getColorForThreshold(index, 0, maxIndex);

  const riskAssessmentData = useMemo(() => {
    const datasets: DatasetType[] = [];
    Object.values(riskAssessment).forEach((value, index) => {
      const color = MapperHelper.getColorForThreshold(index, 0, Math.max(keys.length - 1, 1));
      datasets.push({
        data: [value],
        backgroundColor: color,
        hoverBackgroundColor: color,
        borderSkipped: false,
        datasetIndex: index,
      });
    });
    const total = datasets.reduce((acc, curr) => acc + curr.data[0], 0);
    return {
      labels: ['Risk Assessment'],
      datasets: datasets
        .map(v => ({ ...v, data: [((v.data[0] / total) * 10000) / 100], rawData: v.data[0] }))
        .filter(x => x.data[0] > 0),
    };
  }, [riskAssessment, keys.length]);

  const entries = Object.entries(riskAssessment);
  const total = entries.reduce((acc, [, v]) => acc + v, 0);

  return (
    <div className={styles.wrapper}>
      {hasRiskAssessment ? (
        <>
          <div className={styles.riskAssessmentWrapper}>
            {entries.map(([key, value], index) => {
              const pct = total > 0 ? (value / total) * 100 : 0;
              if (pct === 0) return null;
              return (
                <div
                  key={key}
                  className={styles.status}
                  style={{ flex: pct }}
                >
                  <span
                    className={styles.dot}
                    style={{ backgroundColor: getColor(index) }}
                  />
                  {value} {key.toLowerCase()}
                </div>
              );
            })}
          </div>
          <StackedPill data={riskAssessmentData} />
        </>
      ) : (
        <div className={styles.noData}>No data</div>
      )}
    </div>
  );
};
